import { getAbTestingData } from '@/api/public';
import { IS_US } from '@/utils/constants';
import type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferEnableInput,
  AutoOfferState,
  NormalizedAppliedOffer,
} from './types';

export type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferCouponMode,
  AutoOfferEnableInput,
  AutoOfferPointMode,
  AutoOfferState,
  NormalizedAppliedOffer,
} from './types';

const AUTO_OFFER_EXPERIMENT_VALUE = 2;
/**
 * Cart_Discounts_Auto_Apply_Test = 2 的时候才会应用自动
 * 对照组 - 用户手动 - 应用优惠50% - 1
 * 试验组1 - 系统自动应用 - 优惠50% - 2
 */
const AUTO_OFFER_MANUAL_APPLY_VALUE = 1;
const AUTO_OFFER_AUTO_APPLY_VALUE = 2;
export const PAYMENT_AUTO_OFFER_TOAST_STORAGE_KEY = 'zl-payment-auto-offer-toast';

export const normalizeAmount = (amount?: string | number | null) => {
  if (amount === null || amount === undefined || amount === '') {
    return 0;
  }
  const value = Number(String(amount).replace(/,/g, ''));
  return Number.isFinite(value) ? value : 0;
};

export const shouldEnableManualOffer = ({ subtotal, selectedCount = 0 }: AutoOfferEnableInput) =>
  IS_US && selectedCount > 0 && normalizeAmount(subtotal) > 0;

const shouldAutoApplyOffer = (state?: AutoOfferState | null) =>
  Boolean(state?.enabled) && Number(state?.abValue) === AUTO_OFFER_AUTO_APPLY_VALUE;

const shouldSkipDefaultAutoOffer = (state?: AutoOfferState | null) =>
  Boolean(state?.enabled) && Number(state?.abValue) === AUTO_OFFER_MANUAL_APPLY_VALUE;

/**
 * 改动的时候都需要复测这两个case
 * https://devops.aliyun.com/projex/workitem#viewIdentifier=441e17ad4f72718076eedcf5&openWorkitemIdentifier=7ce93e02d5e745d7a18bcc3149
 * https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/bug#viewIdentifier=1dedc5afd44979211cad516f&openWorkitemIdentifier=36a745c0a1060cab4a20ecec87
 */
export const shouldRestoreAutoCouponOnCartChange = (state?: AutoOfferState | null) =>
  IS_US &&
  Number(state?.abValue) === AUTO_OFFER_AUTO_APPLY_VALUE &&
  ['manual_coupon', 'manual_code'].includes(state?.couponMode);

export const restoreAutoCouponState = (state: AutoOfferState): AutoOfferState => ({
  ...state,
  couponMode: 'auto',
  coupon: undefined,
  code: state.couponMode === 'manual_code' ? state.code : '',
  couponText: '',
  autoCouponCompare: false,
});

export const createDefaultAutoOfferState = ({
  enabled = false,
  abValue = null,
}: {
  enabled?: boolean;
  abValue?: number | string | null;
} = {}): AutoOfferState => ({
  enabled,
  abValue,
  couponMode: 'auto',
  pointMode: 'auto',
  coupon: undefined,
  code: '',
  autoCouponCompare: false,
  expendPoint: undefined,
  applied_offer: normalizeAppliedOffer(undefined),
});

export const hasExplicitAutoOfferSelection = (state?: AutoOfferState | null) =>
  Boolean(state && (state.couponMode !== 'auto' || state.pointMode !== 'auto'));

export const normalizeAppliedOffer = (raw: any): NormalizedAppliedOffer => ({
  enabled: Boolean(raw?.enabled),
  applied: Boolean(raw?.applied),
  autoApplied: Boolean(raw?.auto_applied),
  coupon: {
    source: (raw?.coupon?.source || 'none') as AppliedOfferSource,
    isBest: Boolean(raw?.coupon?.is_best),
    code: raw?.coupon?.code || '',
    detail: raw?.coupon?.detail || {},
  },
  point: {
    source: (raw?.point?.source || 'none') as AppliedOfferSource,
    availablePoint: Number(raw?.point?.available_point || 0),
    maxExpendPoint: Number(raw?.point?.max_expend_point || 0),
    expendPoint: Number(raw?.point?.expend_point || 0),
  },
  reason: raw?.reason || '',
});

const pickNonEmptyString = (...values: any[]) => {
  const value = values.find((item) => typeof item === 'string' && item.trim());
  return value ? value.trim() : '';
};

const getAppliedOfferCode = (appliedOffer?: NormalizedAppliedOffer) => {
  const detail = appliedOffer?.coupon.detail || {};
  return pickNonEmptyString(
    appliedOffer?.coupon.code,
    detail.code,
    detail.discount_code?.code,
    detail.coupon?.code,
    detail.coupon?.discount_code?.code,
  );
};

export const createCheckoutAutoOfferState = (
  state: AutoOfferState,
  rawAppliedOffer?: any,
): AutoOfferState => {
  const applied_offer = rawAppliedOffer
    ? normalizeAppliedOffer(rawAppliedOffer)
    : state.applied_offer;
  const nextState: AutoOfferState = {
    ...state,
    applied_offer,
  };

  if (nextState.couponMode !== 'auto') {
    return nextState;
  }

  const appliedCode = getAppliedOfferCode(applied_offer);
  // 登录后进入支付页需要带着游客阶段手动码做最优券比较。
  // 后端历史字段的 source 不完全固定，只要不是自动最优券，都按手动码兜底透传。
  if (appliedCode && applied_offer?.coupon.source !== 'auto') {
    return {
      ...nextState,
      couponMode: 'manual_code',
      coupon: undefined,
      code: appliedCode,
      couponText: '',
      autoCouponCompare: true,
    };
  }

  return nextState;
};

export const buildAutoOfferParams = (state?: AutoOfferState | null): AutoOfferApiParams => {
  if (!state) {
    return {};
  }

  const params: AutoOfferApiParams = {};

  switch (state.couponMode) {
    case 'auto':
      if (shouldAutoApplyOffer(state)) {
        params.auto_apply_offer = 1;
        params.auto_coupon_compare = 1;
        if (state.code) {
          params.code = state.code;
        }
      } else if (shouldSkipDefaultAutoOffer(state)) {
        params.auto_apply_offer = 1;
        params.auto_coupon_compare = 1;
        params.skip_coupon = 1;
      }
      break;

    case 'skip':
      params.skip_coupon = 1;
      break;

    case 'manual_coupon':
      if (state.coupon) {
        params.coupon = state.coupon;
        if (state.autoCouponCompare) {
          params.auto_apply_offer = 1;
          params.auto_coupon_compare = 1;
        }
      }
      break;

    case 'manual_code':
      if (state.code) {
        params.code = state.code;
        if (state.autoCouponCompare) {
          params.auto_apply_offer = 1;
          params.auto_coupon_compare = 1;
        }
      }
      break;
  }

  if (!state.enabled) {
    return params;
  }

  if (state.pointMode === 'none') {
    params.point = 0;
  } else if (state.pointMode === 'manual' && Number(state.expendPoint) > 0) {
    params.expend_point = Number(state.expendPoint);
  }

  return params;
};

/**
 * 接口文档
 * https://alidocs.dingtalk.com/i/nodes/QG53mjyd80R5bQdxUg3G49M1V6zbX04v
 */
export const buildCartAutoOfferParams = (state?: AutoOfferState | null): AutoOfferApiParams => {
  const params = buildAutoOfferParams(state);
  // 只有北美站不需要 auto_apply_offer 参数，其他都需要
  if (!IS_US || Object.keys(params).length === 0 || params.auto_apply_offer) {
    return params;
  }

  if (params.coupon !== undefined || params.code !== undefined) {
    return {
      ...params,
      auto_apply_offer: 1,
    };
  }

  if (params.skip_coupon) {
    return {
      ...params,
      auto_apply_offer: 1,
    };
  }

  return params;
};

/**
 * 支付页仍会从旧表单里带 coupon/code/point 等参数，同时自动优惠状态也可能表达自动用券、
 * 手动用券/优惠码、跳过优惠券、积分选择等含义。合并自动优惠参数前，先清理旧参数中
 * 可能冲突的优惠字段，避免同一次请求把互斥的优惠指令一起传给后端。
 */
export const mergeAutoOfferParams = <T extends Record<string, any>>(
  params: T,
  autoOfferParams: AutoOfferApiParams,
): T => {
  if (Object.keys(autoOfferParams).length === 0) {
    return params;
  }

  const nextParams = { ...params };

  if (
    autoOfferParams.auto_apply_offer ||
    autoOfferParams.skip_coupon ||
    autoOfferParams.coupon !== undefined ||
    autoOfferParams.code !== undefined
  ) {
    delete nextParams.auto_apply_offer;
    delete nextParams.auto_coupon_compare;
    delete nextParams.coupon;
    delete nextParams.code;
    delete nextParams.skip_coupon;
  }

  if (
    autoOfferParams.auto_apply_offer ||
    autoOfferParams.point !== undefined ||
    autoOfferParams.expend_point !== undefined
  ) {
    delete nextParams.point;
    delete nextParams.expend_point;
  }

  return {
    ...nextParams,
    ...autoOfferParams,
  };
};

export const mergeAppliedOfferIntoState = (
  state: AutoOfferState,
  rawAppliedOffer: any,
): AutoOfferState => {
  const applied_offer = normalizeAppliedOffer(rawAppliedOffer);

  return {
    ...state,
    applied_offer,
  };
};

/**
 * 文档：
 * https://alidocs.dingtalk.com/i/nodes/dpYLaezmVNLZdaXbFGQDp5AP8rMqPxX6?utm_scene=team_space
 * 验证自动应用最佳优惠对购物车结算、订单创建的影响（PC+M）
 * 进组节点：进入购物车
 * 对照组: 用户手动应用优惠
 * 试验组1: 系统自动应用优惠
 */
export const fetchCartAutoOfferAbValue = async () => {
  const AUTO_OFFER_EXPERIMENT_KEY = 'Cart_Discounts_Auto_Apply_Test';
  const { code, data } = await getAbTestingData({
    param_name: AUTO_OFFER_EXPERIMENT_KEY,
  });

  if (code === 200) {
    const abValue = Number(data?.strategy_variable);
    if (Number.isFinite(abValue)) {
      return abValue;
    }
  }
};
